import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';

const styles = {
  container: { minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', paddingBottom: '100px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 },
  backBtn: { background: 'none', border: 'none', fontSize: '16px', color: '#3b82f6', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' },
  content: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' },
  card: { background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' },
  cardHeader: { padding: '14px 16px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white' },
  cardHeaderAmber: { padding: '14px 16px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' },
  cardHeaderSlate: { padding: '14px 16px', background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', color: 'white' },
  cardHeaderBlue: { padding: '14px 16px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white' },
  cardBody: { padding: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#334155' },
  select: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' },
  input: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' },
  inputReadonly: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f1f5f9', boxSizing: 'border-box', fontWeight: 600, textAlign: 'center' },
  textarea: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box', minHeight: '100px', resize: 'vertical' },
  docRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e2e8f0' },
  docLabel: { fontSize: '13px', color: '#1e293b', flex: 1, paddingRight: '12px' },
  docSelect: { width: '140px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', background: 'white' },
  groupBadge: { display: 'inline-block', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: 600 },
  group1Badge: { background: '#fef3c7', color: '#92400e', border: '2px solid #f59e0b' },
  group2Badge: { background: '#dbeafe', color: '#1e40af', border: '2px solid #3b82f6' },
  actions: { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', gap: '12px', padding: '16px', background: 'white', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)' },
  btnSecondary: { flex: 1, padding: '14px', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', background: 'white', border: '2px solid #e2e8f0', color: '#64748b' },
  btnPrimary: { flex: 1, padding: '14px', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none', color: 'white' }
};

const referentiels = [
  "Code de la construction et de l'habitation",
  "Règlement de sécurité ERP du 25 juin 1980",
  "Arrêté du 22 juin 1990",
  "Arrêté du 14 mai 1975"
];

const erpTypes = ["J", "L", "M", "N", "O", "P", "R", "S", "T", "U", "V", "W", "X", "Y"];
const erpCategories = ["1", "2", "3", "4", "5"];
const classificationOrigins = [
  "Déclaration du chef d'établissement",
  "Dossier de permis de construire",
  "Avis de la commission de sécurité",
  "Autre"
];

const documents = [
  { id: "notice_securite", label: "Notice de sécurité" },
  { id: "plans_renseignements", label: "Plans et renseignements de détail concernant les installations techniques" },
  { id: "historique_modifications", label: "Historique des principales modifications effectuées" },
  { id: "prescriptions_commission", label: "Prescriptions notifiées à la suite de visites de contrôle des commissions de sécurité" },
];

const caracteristiques = [
  { id: "locaux_plus_50", label: "Présence de locaux pouvant accueillir plus de 50 personnes" },
  { id: "locaux_plus_100", label: "Présence de locaux pouvant accueillir plus de 100 personnes" },
  { id: "locaux_risque_incendie", label: "Présence de locaux à risque d'incendie" },
  { id: "existence_bapi", label: "Existence de Bloc(s) Autonome(s) Portatif(s) d'Intervention" },
];

function Etape16ERP({ mission, onBack, onSave }) {
  const [formData, setFormData] = useState({
    referentiel_applicable: '',
    establishment_type: '',
    establishment_category: '',
    establishment_group: '',
    classification_origin: '',
    classification_origin_other: '',
    identification_proprietaire: '',
    identification_exploitant: '',
    documentation_presentee: {},
    locaux_non_verifies: '',
    caracteristiques_etablissement: {}
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'rapport_erp_data');
      if (saved) {
        setFormData(prev => ({ ...prev, ...saved }));
      }
    } catch (error) {
      console.error('Erreur chargement ERP:', error);
    }
  };

  const handleChange = (field, value) => {
    let newData = { ...formData, [field]: value };

    // Auto-calcul du groupe selon la catégorie
    if (field === 'establishment_category') {
      if (['1', '2', '3', '4'].includes(value)) {
        newData.establishment_group = '1er groupe';
      } else if (value === '5') {
        newData.establishment_group = '2ème groupe';
      } else {
        newData.establishment_group = '';
      }
    }

    setFormData(newData);
  };

  const handleDocChange = (docId, value) => {
    setFormData(prev => ({
      ...prev,
      documentation_presentee: {
        ...prev.documentation_presentee,
        [docId]: value
      }
    }));
  };

  const handleCaracteristiqueChange = (caracId, value) => {
    setFormData(prev => ({
      ...prev,
      caracteristiques_etablissement: {
        ...prev.caracteristiques_etablissement,
        [caracId]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await inspectionStorage.saveStep(mission.id, 'rapport_erp_data', formData);
      await syncManager.syncStep(mission.id, 'rapport_erp_data', formData);
      
      if (onSave) onSave(formData);
      alert('✅ Informations ERP sauvegardées !');
      onBack();
    } catch (error) {
      console.error('Erreur sauvegarde ERP:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const is1erGroupe = ['1', '2', '3', '4'].includes(formData.establishment_category);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Retour</button>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Étape 16</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>Informations ERP</p>
        </div>
        <div style={{ width: '60px' }}></div>
      </div>

      <div style={styles.content}>
        {/* Référentiel réglementaire */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>📜 Référentiel réglementaire</h2>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Référentiel applicable</label>
              <select
                style={styles.select}
                value={formData.referentiel_applicable}
                onChange={(e) => handleChange('referentiel_applicable', e.target.value)}
              >
                <option value="">-- Sélectionner --</option>
                {referentiels.map(ref => (
                  <option key={ref} value={ref}>{ref}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Classement de l'établissement */}
        <div style={styles.card}>
          <div style={styles.cardHeaderAmber}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>📋 Classement de l'établissement</h2>
          </div>
          <div style={styles.cardBody}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={styles.label}>Type ERP</label>
                <select
                  style={styles.select}
                  value={formData.establishment_type}
                  onChange={(e) => handleChange('establishment_type', e.target.value)}
                >
                  <option value="">--</option>
                  {erpTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Catégorie</label>
                <select
                  style={styles.select}
                  value={formData.establishment_category}
                  onChange={(e) => handleChange('establishment_category', e.target.value)}
                >
                  <option value="">--</option>
                  {erpCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.establishment_group && (
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <span style={{
                  ...styles.groupBadge,
                  ...(is1erGroupe ? styles.group1Badge : styles.group2Badge)
                }}>
                  {formData.establishment_group}
                </span>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Origine du classement</label>
              <select
                style={styles.select}
                value={formData.classification_origin}
                onChange={(e) => handleChange('classification_origin', e.target.value)}
              >
                <option value="">-- Sélectionner --</option>
                {classificationOrigins.map(origin => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
            </div>

            {formData.classification_origin === 'Autre' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Précisez</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.classification_origin_other}
                  onChange={(e) => handleChange('classification_origin_other', e.target.value)}
                  placeholder="Précisez l'origine..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Identification 1er Groupe */}
        {is1erGroupe && (
          <div style={{ ...styles.card, border: '2px solid #f59e0b' }}>
            <div style={styles.cardHeaderAmber}>
              <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>👤 Identification (1er Groupe)</h2>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Identification du propriétaire</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.identification_proprietaire}
                  onChange={(e) => handleChange('identification_proprietaire', e.target.value)}
                  placeholder="Nom et adresse du propriétaire"
                />
              </div>
              <div style={{ ...styles.formGroup, marginBottom: 0 }}>
                <label style={styles.label}>Identification de l'exploitant</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.identification_exploitant}
                  onChange={(e) => handleChange('identification_exploitant', e.target.value)}
                  placeholder="Nom et adresse de l'exploitant (si différent)"
                />
              </div>
            </div>
          </div>
        )}

        {/* Documentation présentée */}
        <div style={styles.card}>
          <div style={styles.cardHeaderSlate}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>📄 Documentation présentée</h2>
          </div>
          <div style={styles.cardBody}>
            {documents.map((doc, index) => (
              <div key={doc.id} style={{
                ...styles.docRow,
                borderBottom: index === documents.length - 1 ? 'none' : '1px solid #e2e8f0'
              }}>
                <span style={styles.docLabel}>{doc.label}</span>
                <select
                  style={styles.docSelect}
                  value={formData.documentation_presentee[doc.id] || ''}
                  onChange={(e) => handleDocChange(doc.id, e.target.value)}
                >
                  <option value="">--</option>
                  <option value="presente">Présenté</option>
                  <option value="non_presente">Non présenté</option>
                  <option value="sans_objet">Sans Objet</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Locaux non vérifiés */}
        <div style={styles.card}>
          <div style={styles.cardHeaderSlate}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>🚫 Locaux non vérifiés</h2>
          </div>
          <div style={styles.cardBody}>
            <textarea
              style={styles.textarea}
              value={formData.locaux_non_verifies}
              onChange={(e) => handleChange('locaux_non_verifies', e.target.value)}
              placeholder="Listez les locaux non vérifiés et les motifs..."
            />
          </div>
        </div>

        {/* Caractéristiques de l'établissement */}
        <div style={styles.card}>
          <div style={styles.cardHeaderBlue}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>🏢 Caractéristiques de l'établissement</h2>
          </div>
          <div style={styles.cardBody}>
            {caracteristiques.map((item, index) => (
              <div key={item.id} style={{
                ...styles.docRow,
                borderBottom: index === caracteristiques.length - 1 ? 'none' : '1px solid #e2e8f0'
              }}>
                <span style={styles.docLabel}>{item.label}</span>
                <select
                  style={styles.docSelect}
                  value={formData.caracteristiques_etablissement[item.id] || ''}
                  onChange={(e) => handleCaracteristiqueChange(item.id, e.target.value)}
                >
                  <option value="">--</option>
                  <option value="oui">Oui</option>
                  <option value="non">Non</option>
                  <option value="sans_objet">Sans objet</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        <button style={styles.btnSecondary} onClick={onBack} disabled={isSaving}>
          Annuler
        </button>
        <button 
          style={{ ...styles.btnPrimary, opacity: isSaving ? 0.6 : 1 }} 
          onClick={handleSave} 
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

export default Etape16ERP;